import { makeStyles } from '@material-ui/core/styles';
import getCommonStyles from './_common';

const usePortfolioStyles = makeStyles((theme) => ({
  ...getCommonStyles(theme),
  root: {
    ...getCommonStyles(theme).sectionContainer,
    background: 'transparent',
    color: theme.palette.text.primary,
    position: 'relative',
    padding: theme.spacing(12, 0, 12),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(8, 0, 8),
    },
  },
  sectionTitle: {
    ...getCommonStyles(theme).sectionTitle,
    fontFamily: '"vester-medium", "Montserrat", "Inter", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.08,
    background: 'linear-gradient(135deg, #ffffff 0%, #cdfaf1 45%, #6fe9d6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: 'none',
    fontSize: '3rem',
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  sectionDescription: {
    ...getCommonStyles(theme).sectionDescription,
    fontFamily: '"Montserrat", "Inter", "Segoe UI", sans-serif',
    color: '#b5c3d3',
    fontSize: '1.1rem',
    lineHeight: 1.85,
    maxWidth: 860,
    margin: '0 auto',
    textAlign: 'center',
    textShadow: 'none',
  },
  portfolioContent: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 4, 6),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },

  '@keyframes portfolioScroll': {
    '0%': { transform: 'translateX(0)' },
    '100%': { transform: 'translateX(-50%)' },
  },

  '@keyframes portfolioBorderOrbit': {
    '0%': { offsetDistance: '0%' },
    '100%': { offsetDistance: '100%' },
  },

  marqueeStage: {
    position: 'relative',
    width: '100%',
    marginTop: theme.spacing(5),
    isolation: 'isolate',
  },

  borderSpark: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 4,
    overflow: 'visible',
  },

  sparkOrbiter: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    offsetPath: 'inset(0 round 10px)',
    offsetRotate: 'auto',
    animation: '$portfolioBorderOrbit 14s linear infinite',
    willChange: 'offset-distance',
    [theme.breakpoints.down('md')]: {
      animationDuration: '11s',
    },
    [theme.breakpoints.down('sm')]: {
      animationDuration: '8s',
    },
  },

  sparkStar: {
    position: 'absolute',
    left: -185,
    top: -25,
    width: 220,
    height: 50,
    overflow: 'visible',
    filter:
      'drop-shadow(0 0 4px rgba(255, 255, 255, 0.7)) drop-shadow(0 0 10px rgba(77, 244, 189, 0.55)) drop-shadow(0 0 26px rgba(64, 121, 191, 0.35))',
  },

  marqueeWrap: {
    position: 'relative',
    width: '100%',
    padding: theme.spacing(3.5, 0),
    background:
      'linear-gradient(135deg, #ffffff 0%, #f4f8fd 45%, #e8eff8 100%)',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
    boxShadow:
      '0 -1px 0 rgba(255, 255, 255, 0.06), 0 30px 60px rgba(0, 0, 0, 0.35)',
    overflow: 'hidden',
    maskImage:
      'linear-gradient(to right, transparent 0, #000 96px, #000 calc(100% - 96px), transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(to right, transparent 0, #000 96px, #000 calc(100% - 96px), transparent 100%)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 0),
      maskImage:
        'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
      WebkitMaskImage:
        'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
    },
  },

  marqueeRow: {
    width: '100%',
    overflow: 'hidden',
    padding: theme.spacing(1.5, 0),
    '&:hover $marqueeTrack': {
      animationPlayState: 'paused',
    },
  },

  marqueeTrack: {
    display: 'flex',
    alignItems: 'center',
    width: 'max-content',
    gap: theme.spacing(7),
    animation: '$portfolioScroll 38s linear infinite',
    willChange: 'transform',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(5),
      animationDuration: '30s',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(4),
      animationDuration: '24s',
    },
  },

  marqueeTrackReverse: {
    animationDirection: 'reverse',
  },

  marqueeItem: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 80,
    minWidth: 160,
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down('sm')]: {
      height: 56,
      minWidth: 120,
    },
  },

  marqueeLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    textDecoration: 'none',
    color: 'inherit',
    borderRadius: 10,
    transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)',
    '&:hover': {
      transform: 'scale(1.06)',
    },
    '&:focus-visible': {
      outline: '2px solid rgba(0, 212, 170, 0.55)',
      outlineOffset: 4,
    },
  },

  marqueeLogo: {
    maxHeight: '100%',
    maxWidth: 180,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    filter: 'grayscale(0.25)',
    opacity: 0.85,
    transition: 'filter 240ms ease, opacity 240ms ease, transform 240ms ease',
    '$marqueeItem:hover &': {
      filter: 'grayscale(0)',
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: 130,
    },
  },

  // Legacy classes kept for backward compatibility with other files referencing them
  paper: {
    position: 'absolute',
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  portfolioItem: {
    background: 'transparent',
  },
  media: {
    backgroundSize: 'contain',
  },
}));
export default usePortfolioStyles;
